/**
 * Team Management API Client
 * 
 * API calls for team management features.
 * Part of E007-S026: Team Management & User Administration
 */

import { apiClient } from './apiClient';
import type {
  UsersListResponse,
  InviteUserRequest,
  InviteUserResponse,
  UpdateUserRequest,
  TeamMember,
  ActivityResponse,
  UsersFilters,
  ActivityFilters
} from '../types/team';

const BASE_URL = '/team';

/**
 * List team members with optional filtering
 */
export async function listUsers(filters?: UsersFilters): Promise<UsersListResponse> {
  const params = new URLSearchParams();
  
  if (filters?.search) params.append('search', filters.search);
  if (filters?.role) params.append('role', filters.role);
  if (filters?.status) params.append('status', filters.status);
  if (filters?.page) params.append('page', filters.page.toString());
  if (filters?.limit) params.append('limit', filters.limit.toString());
  
  const queryString = params.toString();
  const url = queryString ? `${BASE_URL}/users?${queryString}` : `${BASE_URL}/users`;
  
  const response = await apiClient.get<UsersListResponse>(url);
  return response.data;
}

/**
 * Invite a new user to the organization
 */
export async function inviteUser(request: InviteUserRequest): Promise<InviteUserResponse> {
  const response = await apiClient.post<InviteUserResponse>(
    `${BASE_URL}/invite`,
    request
  );
  return response.data;
}

/**
 * Update an existing user
 */
export async function updateUser(
  userId: string,
  request: UpdateUserRequest
): Promise<TeamMember> {
  const response = await apiClient.patch<TeamMember>(
    `${BASE_URL}/users/${userId}`,
    request
  );
  return response.data;
}

/**
 * Deactivate a user (soft delete)
 */
export async function deactivateUser(userId: string): Promise<void> {
  await apiClient.delete(`${BASE_URL}/users/${userId}`);
}

/**
 * Get activity log for a user
 */
export async function getUserActivity(
  userId: string,
  filters?: ActivityFilters
): Promise<ActivityResponse> {
  const params = new URLSearchParams();
  
  if (filters?.start_date) params.append('start_date', filters.start_date);
  if (filters?.end_date) params.append('end_date', filters.end_date);
  if (filters?.page) params.append('page', filters.page.toString());
  if (filters?.limit) params.append('limit', filters.limit.toString());
  
  const queryString = params.toString();
  const url = queryString 
    ? `${BASE_URL}/users/${userId}/activity?${queryString}`
    : `${BASE_URL}/users/${userId}/activity`;
  
  const response = await apiClient.get<ActivityResponse>(url);
  return response.data;
}
